/**
 * Currency formatting utilities
 *
 * Note: All financial calculations must happen on the backend with Decimal precision.
 * Frontend only displays currency values, never performs calculations.
 */
import { getAppNumberLocale } from "./appNumberLocale";

export { getAppNumberLocale } from "./appNumberLocale";

/** Return the currency suffix based on current language: دج for Arabic, DA otherwise. */
function currencySuffix(): string {
  return getAppNumberLocale() === "ar-DZ" ? "دج" : "DA";
}

/**
 * Format amount as DZD currency
 *
 * @param amount - Numeric amount to format
 * @returns Formatted currency string
 *
 * @example
 * formatCurrency(10000.50) // "10 000,50 DA"  (en/fr)
 * formatCurrency(10000.50) // "10 000,50 دج"  (ar)
 */
export function formatCurrency(amount: number): string {
  const n = Number(amount);
  const safe = Number.isFinite(n) ? n : 0;
  const locale = getAppNumberLocale();
  const formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(safe);
  return `${formatted} ${currencySuffix()}`;
}

/**
 * Format amount as compact currency (K, M abbreviations)
 *
 * @param amount - Numeric amount to format
 * @returns Compact formatted currency string
 *
 * @example
 * formatCurrencyCompact(10000) // "10K DA"
 * formatCurrencyCompact(1500000) // "1.5M DA"
 */
export function formatCurrencyCompact(amount: number): string {
  const n = Number(amount);
  const safe = Number.isFinite(n) ? n : 0;
  const locale = getAppNumberLocale();
  const formatted = new Intl.NumberFormat(locale, {
    notation: "compact",
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(safe);
  return `${formatted} ${currencySuffix()}`;
}

/**
 * Parse formatted currency string to number
 * WARNING: Only use for display parsing, never for calculations!
 *
 * @param currencyString - Formatted currency string
 * @returns Numeric value
 */
export function parseCurrency(currencyString: string): number {
  // Remove currency symbol, spaces, and replace comma with period
  const cleaned = currencyString
    .replace(/[^\d,.-]/g, "")
    .replace(",", ".");
  return parseFloat(cleaned) || 0;
}
